import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  UseFieldArrayReturn,
  UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { useSelectedEmployeeId } from '~/hooks/employee';
import { useVisible } from '~/hooks/useVisible';

import { CompanyFormType, EmployeeFormType } from '../SharedIndex';
import { useGetCompanyForms } from '../SharedIndex/useGetCompanyForms';
import { useGetEmployeeForms } from '../SharedIndex/useGetEmployeeForms';

import { mapDefaultData } from './data/dataMapping';
import { dataSaving } from './data/dataSaving';

export type FixForKeyIssue = {
  [key: string]: number | string | null | boolean | File | undefined;
};

export interface RowData extends FixForKeyIssue {
  fEmpFormId: number;
  fEmpFormEmpId?: number;
  fEmpFormCompFormFK?: number;
  fEmpFormCreatedTime?: string;
  fEmpFormUpdatedTime?: string;
  fEmpFormInfo?: string;
  fEmpFormAttachmentId: number;
  fEmpFormAttachmentName?: string;
  fFormAttachment?: File | undefined;
}

export type IFormInput = {
  form: RowData[];
};

export type UseFormsOutput = {
  employeeForms: EmployeeFormType[];
  defaultData: RowData[];
  useFormMethods: UseFormReturn<IFormInput>;
  companyForms: CompanyFormType[];
  setEditedRows: Dispatch<SetStateAction<number[]>>;
  setDeletedRows: Dispatch<SetStateAction<number[]>>;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  openDiaolog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  onSubmit: () => void;
  useFieldArrayMethods: UseFieldArrayReturn<IFormInput, 'form', 'id'>;
  isFormsDialogOpen: boolean;
  closeFormsDialog: () => void;
  openFormsDialog: () => void;
};

export const useForms = (): UseFormsOutput => {
  const [defaultData, setDefaultData] = useState<RowData[]>([]);
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const empId = String(selectedEmployeeId);
  const useFormMethods = useForm<IFormInput>();
  const { handleSubmit, control, reset } = useFormMethods;
  const [editedRows, setEditedRows] = useState<number[]>([]);
  const [deletedRows, setDeletedRows] = useState<number[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [openDiaolog, setOpenDialog] = useState<boolean>(false);
  const useFieldArrayMethods = useFieldArray({ name: 'form', control });
  const { data: employeeForms, mutate: mutateEmployeeForms } =
    useGetEmployeeForms(Number(empId));
  const { data: companyForms } = useGetCompanyForms();

  useEffect(() => {
    if (employeeForms) {
      const newDefaultData = mapDefaultData(employeeForms);
      setDefaultData(newDefaultData);
      reset({
        form: newDefaultData,
      });
    }
  }, [employeeForms, reset]);

  const {
    isVisible: isFormsDialogOpen,
    show: openFormsDialog,
    hide: closeFormsDialog,
  } = useVisible();

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const rowData = data.form;
      (async () => {
        dataSaving(
          parseInt(empId),
          deletedRows,
          editedRows,
          defaultData,
          rowData,
        ).then(() => {
          setEditMode(!editMode);
          mutateEmployeeForms();
        });
      })();
    });
  }, [
    handleSubmit,
    empId,
    deletedRows,
    editedRows,
    defaultData,
    editMode,
    mutateEmployeeForms,
  ]);

  return {
    employeeForms,
    defaultData,
    useFormMethods,
    setEditedRows,
    setDeletedRows,
    editMode,
    setEditMode,
    onSubmit: handleFormSubmit,
    useFieldArrayMethods,
    companyForms,
    openDiaolog,
    setOpenDialog,
    closeFormsDialog,
    isFormsDialogOpen,
    openFormsDialog,
  };
};
